import {
  Group,
  TextInput,
  Divider,
  ActionIcon,
  Text,
  Box,
  ThemeIcon,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useDefaultSuspenseInfiniteQuery } from "../../../../utils/useDefaultSuspenseInfiniteQuery"
import {
  DefaultInfiniteList,
  DefaultInfiniteListProps,
} from "../../../../-components/InfiniteList"
import { ChatListItem } from "./ChatListItem"
import { useNavigate, useParams } from "@tanstack/react-router"
import { useDebouncedValue } from "@mantine/hooks"
import { ChangeEvent } from "react"
import {
  InfiniteData,
  UseSuspenseInfiniteQueryResult,
} from "@tanstack/react-query"
import { Pagination } from "../../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { ChatType } from "../../../../orval/gen/model"
import { CreateChatMenu } from "../CreateChatMenu"
import { IconArchive, IconChevronLeft } from "@tabler/icons-react"
import {
  getChats,
  getGetChatsQueryKey,
  useUserHasArchivedChatSuspense,
} from "@/orval/gen"
import { ChatDto } from "@/orval/gen/model"

interface Props {
  listProps?: Partial<
    Omit<
      DefaultInfiniteListProps<
        true,
        UseSuspenseInfiniteQueryResult<
          InfiniteData<Pagination<ChatDto>, unknown>
        >
      >,
      "queryData"
    >
  >
  smallLayout: boolean
}

export function ChatListForCurrentUser({ listProps, smallLayout }: Props) {
  const { t } = useTranslation("app_server")

  const [nameSearch, setNameSearch] = React.useState("")
  const [debouncedNameSearch] = useDebouncedValue(nameSearch, 500)

  const { chatId } = useParams({ strict: false })

  const navigate = useNavigate()
  const [inactive, setInactive] = React.useState(false)
  const request = {
    nameSearch: debouncedNameSearch,
    chatTypeFilters: [
      ChatType.Direct,
      ChatType.PrivateGroup,
      ChatType.PublicGroup,
      ChatType.Broadcast,
    ],
    inactive,
  }
  const stableQueryResult = useDefaultSuspenseInfiniteQuery(
    getChats,
    (pageRequest) => ({
      ...pageRequest,
      ...request,
    }),
    { pageSize: 50, queryKey: getGetChatsQueryKey(request) }
  )

  const { data: userHasArchivedChat } = useUserHasArchivedChatSuspense()

  React.useEffect(() => {
    if (!userHasArchivedChat) {
      setInactive(false)
    }
  }, [userHasArchivedChat])

  return (
    <>
      <Group>
        <TextInput
          flex={1}
          onChange={(evt: ChangeEvent<HTMLInputElement>) =>
            setNameSearch(evt.currentTarget.value)
          }
          value={nameSearch}
          placeholder={t("SearchChat")}
        />
        <CreateChatMenu
          variant="chat"
          label={smallLayout ? undefined : t("NewChat")}
        />
      </Group>
      <Divider />
      {userHasArchivedChat && (
        <Box
          style={{ cursor: "pointer" }}
          onClick={() => setInactive(!inactive)}
        >
          {inactive ? (
            <Group>
              <ActionIcon
                size={"sm"}
                c="dimmed"
                variant="transparent"
                onClick={() => setInactive(false)}
              >
                <IconChevronLeft />
              </ActionIcon>
              <Text size="sm" c="dimmed">
                {t("Archived")}
              </Text>
            </Group>
          ) : (
            <Group>
              <ThemeIcon size={"sm"} c="dimmed" variant="transparent">
                <IconArchive />
              </ThemeIcon>
              <Text variant="transparent" size="sm" c="dimmed">
                {t("ShowArchived")}
              </Text>
            </Group>
          )}
        </Box>
      )}
      <DefaultInfiniteList
        selectable
        queryResult={stableQueryResult}
        renderItem={({ index: _, entity, selectionState, containerRef }) => (
          <ChatListItem
            selectionOverride={entity.chatId.toString() === chatId}
            onClick={() =>
              navigate({
                to: "/app/com/chat/$chatId",
                params: { chatId: entity.chatId.toString() },
              })
            }
            entity={entity}
            selectionState={selectionState}
            key={entity.chatId.toString()}
            scrollContainerRef={containerRef}
            touchMenuEnabled={smallLayout}
          />
        )}
        {...listProps}
      />
    </>
  )
}
